import { MDBBtn, MDBIcon, MDBSpinner } from "mdb-react-ui-kit";
import { ParamData } from "./AdvancedSettings";
import { useCallback, useEffect, useMemo, useState } from "react";
import { deepEqual } from "../../../service/utils";
import { ASParamEditor } from "./ASParamEditor";

export interface ASParamViewerProps {
    param: ParamData;
    icon: string;
    onRead: () => void;
    onWrite: (val: any) => void;
}

export const ASParamViewer = ({
    param,
    icon,
    onRead,
    onWrite
}: ASParamViewerProps) => {
    const [currentValue, setCurrentValue] = useState<any>(param.value);
    const [expectValueChange, setExpectValueChange] = useState(false);
    const [changed, setChanged] = useState(false);

    // const equal = useMemo(() => deepEqual(currentValue, param.value), [currentValue, param.value]);

    const canRead = useMemo(() => !param.reading && !param.writing, [param]);
    const canWrite = useMemo(() => !param.reading && !param.writing && changed, [param, changed]);
    const canReset = useMemo(() => canWrite, [canWrite]);
    const canEdit = useMemo(() => !param.reading && !param.writing && currentValue !== null && !param.param.readOnly, [param, currentValue]);

    const onReset = useCallback(() => {
        setCurrentValue(param.value);
    }, [param]);

    const onRealValueChange = useCallback((newValue: any) => {
        if (currentValue == null || expectValueChange) setCurrentValue(newValue);
        setExpectValueChange(false);
    }, [currentValue, expectValueChange]);

    useEffect(() => {
        onRealValueChange(param.value);
    }, [param.value]);

    useEffect(() => {
        if (param.reading) setExpectValueChange(true);
        else if (expectValueChange) {
            onRealValueChange(param.value);
        }
    }, [param.value, param.reading, expectValueChange]);

    return (
        <div className="d-flex flex-column p-3 flex-fill h-100">
            <div className="d-flex flex-row align-items-center w-100 overflow-hidden" style={{}}>
                <div className="d-flex flex-column overflow-hidden pe-3">
                    <div className="d-flex flex-row align-items-center overflow-hidden">
                        <MDBIcon icon={icon} className="me-2" size="lg"/>
                        <h5 className="me-2 mb-0" style={{whiteSpace: "nowrap", textOverflow: "clip"}}>{param.param.name}</h5>
                        {param.param.readOnly && <span className="badge badge-primary">Read-only</span> }
                    </div>

                    <div className="d-flex flex-row align-items-center mt-3">
                        <MDBBtn
                            color="primary"
                            className="me-2 px-0 d-flex flex-row justify-content-center"
                            style={{width: 120}}
                            disabled={!canRead}
                            onClick={() => onRead()}
                        >
                            <span style={{display: param.reading ? "none" : "block"}}><MDBIcon icon="upload" className="me-1"/></span>
                            <MDBSpinner role="status" size="sm" className="me-1" style={{display: param.reading ? "block" : "none"}}/>
                            {param.reading ? "Reading" : "Read"}
                        </MDBBtn>
                        <MDBBtn
                            color="success"
                            className={`me-2 px-0 d-flex flex-row justify-content-center${param.param.readOnly ? " d-none":""}`}
                            style={{width: 120}}
                            disabled={!canWrite}
                            onClick={() => onWrite(currentValue)}
                        >
                            <span style={{display: param.writing ? "none" : "block"}}><MDBIcon icon="download" className="me-1"/></span>
                            <MDBSpinner role="status" size="sm" className="me-1" style={{display: param.writing ? "block" : "none"}}/>
                            {param.writing ? "Writing" : "Write"}
                        </MDBBtn>

                        <MDBBtn
                            color="link"
                            style={{display: canReset ? "block" : "none"}}
                            onClick={onReset}
                        >
                            Reset
                        </MDBBtn>
                    </div>
                </div>
            </div>
            
            <div className="d-flex justify-content-start w-100 mt-2 pt-2 px-1 overflow-auto" style={{borderTop: '1px solid rgba(0,0,0,0.1)', flex: 1}}>
                <ASParamEditor
                    key={param.param.addr}
                    param={param.param}
                    realValue={param.value}
                    currentValue={currentValue}
                    onCurrentValueChange={val => {
                        setCurrentValue(val);
                        setChanged(true);
                    }}
                    disabled={!canEdit}
                />
            </div>
            
        </div>
    )
}